import { useNavigate } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  CreateProcurementTypeDocument,
  NewRequestPageDocument,
  ProcurementSettingsPageDocument,
  ProcurementTypesDataCollectionDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface CreateRequestTypeForm {
  name: string
  description: string
}

interface Props {
  onClose: () => void
  hasFormsEnabled: boolean
}

const CreateRequestTypeModal = ({ onClose, hasFormsEnabled }: Props) => {
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const [createProcurementType] = useMutation(CreateProcurementTypeDocument, {
    extractorFn: (data) => data.procurementMutations.createProcurementType,
  })

  const form = useForm<CreateRequestTypeForm>({
    defaultValues: { name: '', description: '' },
    validate: () => {
      const errors: FormErrors<CreateRequestTypeForm> = {}
      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await createProcurementType({
        variables: { companyId: company.id, description: data.description, name: data.name },

        refetchQueries: [
          ProcurementSettingsPageDocument,
          NewRequestPageDocument,
          ProcurementTypesDataCollectionDocument,
        ],
      })

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        handleClose()
        const typeId = handled.data?.type?.id
        if (typeId && hasFormsEnabled) {
          const searchParams = new URLSearchParams({ tab: 'form' })
          navigate({
            pathname: navigation.settings.procurement.requestType.id.fullPath({ id: typeId }),
            search: searchParams.toString(),
          })
        }
      }
      return handled
    },
  })

  const handleSave = async () => {
    form.handleSubmit()
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <Modal
      illustration="input-field"
      title={i18n.t('procurement.purchase_requests.settings.types.modals.detail.title')}
      description={i18n.t('procurement.purchase_requests.settings.types.modals.detail.description')}
      footer={
        <>
          <Button type="secondary" onClick={handleClose}>
            {i18n.t('procurement.purchase_requests.settings.types.modals.detail.buttons.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={form.isSubmitting || !form.dirty}>
            {i18n.t('procurement.purchase_requests.settings.types.modals.detail.buttons.save')}
          </Button>
        </>
      }
      onClose={onClose}
    >
      <Box gap="s16">
        <TextField
          label={i18n.t('procurement.purchase_requests.settings.types.modals.detail.labels.name')}
          onChange={(name) => form.setValues({ ...form.values, name })}
          value={form.values.name}
          error={form.errors.name}
          mandatory
        />
        <TextField
          label={i18n.t(
            'procurement.purchase_requests.settings.types.modals.detail.labels.description'
          )}
          description={i18n.t(
            'procurement.purchase_requests.settings.types.modals.detail.descriptions.length'
          )}
          onChange={(description) => form.setValues({ ...form.values, description })}
          value={form.values.description}
          maxLength={75}
        />
      </Box>
    </Modal>
  )
}

export default CreateRequestTypeModal
